import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MdOutlineFileDownload,
  MdPlayArrow,
  MdMoreVert,
  MdOutlineDelete,
  MdOutlineSettings,
  MdCheckCircle,
  MdCheck,
  MdKeyboardArrowDown,
  MdOutlineExplore,
  MdOutlineFolder,
  MdOutlineStorage,
  MdClose,
} from 'react-icons/md';
import { BiSolidDownload } from 'react-icons/bi';
import Toast from './Toast';
import ConfirmModal from './ConfirmModal';
import {
  getDownloadedVideos,
  removeDownloadedVideo,
  clearAllDownloads,
  getDownloadSettings,
  saveDownloadSettings,
  calculateStorageStats,
  QUALITY_PRESETS,
} from './utils/downloadUtils';
import { API_BASE_URL, normalizeMediaUrl } from './config';
import './DownloadsPage.css';

const SORT_OPTIONS = [
  { id: 'recent', label: 'Recently downloaded' },
  { id: 'title_asc', label: 'Title (A-Z)' },
  { id: 'size_desc', label: 'Largest file' },
];

export default function DownloadsPage({ userProfile, onNavigate }) {
  const [downloads, setDownloads] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSettingsClosing, setIsSettingsClosing] = useState(false);
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
  const [downloadSettings, setDownloadSettings] = useState(getDownloadSettings);
  const [toast, setToast] = useState({ isOpen: false, message: '', icon: null });

  const sortRef = useRef(null);
  const menuRef = useRef(null);
  const settingsClosingTimerRef = useRef(null);

  const openSettingsModal = () => {
    if (settingsClosingTimerRef.current) clearTimeout(settingsClosingTimerRef.current);
    setIsSettingsClosing(false);
    setIsSettingsOpen(true);
  };

  const closeSettingsModal = () => {
    if (isSettingsClosing) return;
    setIsSettingsClosing(true);
    settingsClosingTimerRef.current = setTimeout(() => {
      setIsSettingsOpen(false);
      setIsSettingsClosing(false);
    }, 220);
  };

  // Close modal on Escape key
  useEffect(() => {
    if (!isSettingsOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeSettingsModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSettingsOpen, isSettingsClosing]);

  useEffect(() => {
    return () => {
      if (settingsClosingTimerRef.current) clearTimeout(settingsClosingTimerRef.current);
    };
  }, []);

  const showToast = (message, icon = null) => {
    setToast({ isOpen: false, message: '', icon: null });
    setTimeout(() => {
      setToast({ isOpen: true, message, icon });
    }, 50);
  };

  // Load downloads from storage
  const loadDownloads = useCallback(() => {
    const list = getDownloadedVideos();
    setDownloads(list);
  }, []);

  useEffect(() => {
    loadDownloads();

    const handleUpdate = () => {
      loadDownloads();
      setDownloadSettings(getDownloadSettings());
    };

    window.addEventListener('speedotube_downloads_updated', handleUpdate);
    window.addEventListener('speedotube_download_settings_updated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('speedotube_downloads_updated', handleUpdate);
      window.removeEventListener('speedotube_download_settings_updated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [loadDownloads]);

  // Handle outside clicks for menus
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setIsSortOpen(false);
      }
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setActiveMenuId(null);
      }
    };
    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format relative download time
  const formatDownloadTime = (dateStr) => {
    if (!dateStr) return 'Recently';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) {
      const mins = Math.floor(diff / 60);
      return `${mins}m ago`;
    }
    if (diff < 86400) {
      const hrs = Math.floor(diff / 3600);
      return `${hrs}h ago`;
    }
    const days = Math.floor(diff / 86400);
    return `${days}d ago`;
  };

  // Sort downloads
  const sortedDownloads = [...downloads].sort((a, b) => {
    if (sortBy === 'title_asc') {
      return (a.title || '').localeCompare(b.title || '');
    }
    if (sortBy === 'size_desc') {
      return (b.sizeMb || 0) - (a.sizeMb || 0);
    }
    // 'recent'
    return new Date(b.downloadedAt || 0) - new Date(a.downloadedAt || 0);
  });

  const handlePlayVideo = (videoUrlId) => {
    if (onNavigate) {
      onNavigate(`/watch?v=${videoUrlId}`);
    }
  };

  const handleRemoveDownload = (e, videoId, videoTitle) => {
    e.stopPropagation();
    setActiveMenuId(null);
    removeDownloadedVideo(videoId);
    showToast(`Removed "${videoTitle || 'video'}" from Downloads`, <MdOutlineDelete size={18} />);
  };

  const handleClearAll = () => {
    clearAllDownloads();
    setIsDeleteAllModalOpen(false);
    showToast('All downloads deleted', <MdOutlineDelete size={18} />);
  };

  const handleSaveSettings = (key, value) => {
    const updated = { ...downloadSettings, [key]: value };
    setDownloadSettings(updated);
    saveDownloadSettings(updated);
  };

  const storageStats = calculateStorageStats(downloads);

  return (
    <div className="yt-downloads-page">
      {/* Top Header Section */}
      <div className="yt-downloads-header">
        <div className="yt-downloads-title-area">
          <div className="yt-downloads-title-row">
            <h1 className="yt-downloads-main-title">Downloads</h1>
            {downloads.length > 0 && (
              <span className="yt-downloads-count-pill">
                {downloads.length} {downloads.length === 1 ? 'video' : 'videos'}
              </span>
            )}
          </div>
          <p className="yt-downloads-subtitle">
            Downloads remain available as long as your device has an active internet connection at least once every 30 days.
          </p>
        </div>

        <div className="yt-downloads-actions-bar">
          {/* Sort Selector (Shown when > 1 downloads) */}
          {downloads.length > 1 && (
            <div className="yt-downloads-sort-wrap" ref={sortRef}>
              <span className="yt-downloads-sort-label">Sort by:</span>
              <button
                type="button"
                className={`yt-downloads-sort-trigger ${isSortOpen ? 'open' : ''}`}
                onClick={() => setIsSortOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={isSortOpen}
              >
                <span className="yt-downloads-sort-value">
                  {SORT_OPTIONS.find((o) => o.id === sortBy)?.label || 'Recently downloaded'}
                </span>
                <MdKeyboardArrowDown
                  size={18}
                  className={`yt-downloads-sort-chevron ${isSortOpen ? 'rotated' : ''}`}
                />
              </button>

              {isSortOpen && (
                <div className="yt-downloads-sort-dropdown-menu">
                  {SORT_OPTIONS.map((opt) => {
                    const isSelected = sortBy === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={`yt-downloads-sort-item ${isSelected ? 'active' : ''}`}
                        onClick={() => {
                          setSortBy(opt.id);
                          setIsSortOpen(false);
                        }}
                      >
                        <span className="yt-downloads-sort-check">
                          {isSelected && <MdCheck size={16} />}
                        </span>
                        <span className="yt-downloads-sort-item-label">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Download Settings Button */}
          <button
            className="yt-downloads-settings-btn"
            onClick={openSettingsModal}
            title="Download settings"
          >
            <MdOutlineSettings size={19} />
            <span>Download settings</span>
          </button>

          {/* Delete All Downloads Button (Only when downloads exist) */}
          {downloads.length > 0 && (
            <button
              className="yt-downloads-delete-all-btn"
              onClick={() => setIsDeleteAllModalOpen(true)}
              title="Delete all downloads"
            >
              <MdOutlineDelete size={19} />
              <span>Delete all</span>
            </button>
          )}
        </div>
      </div>

      {/* Storage Indicator Strip (Shown when downloads exist) */}
      {downloads.length > 0 && (
        <div className="yt-downloads-storage-strip">
          <div className="yt-downloads-storage-bar-wrap">
            <div
              className="yt-downloads-storage-bar-fill"
              style={{ width: `${Math.max(2, storageStats.percentageUsed)}%` }}
            />
          </div>
          <div className="yt-downloads-storage-labels">
            <span className="yt-downloads-storage-item">
              <span className="yt-storage-dot used" />
              SpeedoTube downloads: <strong>{storageStats.usedFormatted}</strong>
            </span>
            <span className="yt-bullet">•</span>
            <span className="yt-downloads-storage-item">
              <span className="yt-storage-dot free" />
              Available space: <strong>{storageStats.freeFormatted}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Populated Video List */}
      {downloads.length > 0 && (
        <div className="yt-downloads-list">
          {sortedDownloads.map((item, idx) => (
            <div
              key={item.id}
              className="yt-downloads-card"
              onClick={() => handlePlayVideo(item.videoUrlId)}
            >
              <div className="yt-downloads-thumb-wrap">
                <img
                  src={normalizeMediaUrl(item.thumbnailUrl) || normalizeMediaUrl('/drive/video_thumbnails/thumbnail-157.jpeg')}
                  alt={item.title}
                  className="yt-downloads-thumb-img"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = normalizeMediaUrl('/drive/video_thumbnails/thumbnail-157.jpeg') || `${process.env.PUBLIC_URL || ''}/img/banner.png`;
                  }}
                />
                <span className="yt-downloads-duration">{item.duration}</span>
                <div className="yt-downloads-status-badge" title="Available offline">
                  <MdCheckCircle size={13} className="yt-downloads-badge-icon" />
                  <span>Downloaded</span>
                </div>
                <div className="yt-downloads-hover-play">
                  <MdPlayArrow size={34} />
                  <span>PLAY</span>
                </div>
              </div>

              <div className="yt-downloads-info">
                <div className="yt-downloads-title-row">
                  <h3 className="yt-downloads-title" title={item.title}>
                    {item.title}
                  </h3>
                  <div className="yt-downloads-menu-wrap" ref={activeMenuId === item.id ? menuRef : null}>
                    <button
                      className="yt-downloads-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(activeMenuId === item.id ? null : item.id);
                      }}
                      aria-label="Download options"
                    >
                      <MdMoreVert size={20} />
                    </button>

                    {activeMenuId === item.id && (
                      <div className="yt-downloads-dropdown-menu">
                        <button
                          className="yt-downloads-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMenuId(null);
                            handlePlayVideo(item.videoUrlId);
                          }}
                        >
                          <MdPlayArrow size={18} />
                          <span>Play video</span>
                        </button>
                        <button
                          className="yt-downloads-dropdown-item delete"
                          onClick={(e) => handleRemoveDownload(e, item.id, item.title)}
                        >
                          <MdOutlineDelete size={18} />
                          <span>Delete from downloads</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <p className="yt-downloads-channel">{item.channelName}</p>

                <div className="yt-downloads-meta">
                  <span className="yt-downloads-views">{item.views}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-downloads-time">{formatDownloadTime(item.downloadedAt)}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-downloads-quality-chip">{item.quality}</span>
                  <span className="yt-bullet">•</span>
                  <span className="yt-downloads-size">{item.size}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* YouTube Exact Empty State */}
      {downloads.length === 0 && (
        <div className="yt-downloads-empty-state">
          <div className="yt-downloads-empty-icon-box">
            <MdOutlineFileDownload size={48} className="yt-downloads-empty-icon" />
          </div>
          <h2 className="yt-downloads-empty-title">No downloads yet</h2>
          <p className="yt-downloads-empty-desc">
            Videos you download will show up here so you can watch them offline.
          </p>
          <button
            className="yt-downloads-empty-action-btn"
            onClick={() => onNavigate && onNavigate('/')}
          >
            <MdOutlineExplore size={20} />
            <span>Explore videos</span>
          </button>
        </div>
      )}

      {/* Download Settings Modal */}
      {isSettingsOpen && (
        <div
          className={`yt-downloads-modal-overlay ${isSettingsClosing ? 'exiting' : ''}`}
          onClick={closeSettingsModal}
        >
          <div
            className={`yt-downloads-modal-container ${isSettingsClosing ? 'exiting' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="yt-downloads-modal-header">
              <div className="yt-modal-title-group">
                <MdOutlineSettings size={22} className="yt-modal-header-icon" />
                <h3 className="yt-downloads-modal-title">Download Settings</h3>
              </div>
              <button
                className="yt-downloads-modal-close-btn"
                onClick={closeSettingsModal}
                aria-label="Close"
              >
                <MdClose size={22} />
              </button>
            </div>

            <div className="yt-downloads-modal-body">
              {/* Quality Preference */}
              <div className="yt-settings-section">
                <label className="yt-settings-section-title">Download Quality</label>
                <p className="yt-settings-section-desc">
                  Choose the default video quality for downloads on this device.
                </p>
                <div className="yt-quality-options-list">
                  <label className="yt-quality-radio-label">
                    <input
                      type="radio"
                      name="downloadQuality"
                      value="ask"
                      checked={downloadSettings.defaultQuality === 'ask'}
                      onChange={() => handleSaveSettings('defaultQuality', 'ask')}
                    />
                    <span>Ask each time</span>
                  </label>
                  {QUALITY_PRESETS.map((q) => (
                    <label key={q.id} className="yt-quality-radio-label">
                      <input
                        type="radio"
                        name="downloadQuality"
                        value={q.id}
                        checked={downloadSettings.defaultQuality === q.id}
                        onChange={() => handleSaveSettings('defaultQuality', q.id)}
                      />
                      <span>{q.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Smart Downloads Toggle */}
              <div className="yt-settings-section">
                <div className="yt-settings-toggle-row">
                  <div>
                    <label className="yt-settings-section-title">Smart downloads</label>
                    <p className="yt-settings-section-desc">
                      Automatically download recommended videos to keep you entertained offline.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="yt-settings-toggle-checkbox"
                    checked={!!downloadSettings.smartDownloads}
                    onChange={(e) => handleSaveSettings('smartDownloads', e.target.checked)}
                  />
                </div>
              </div>

              {/* Storage Info */}
              <div className="yt-settings-section">
                <label className="yt-settings-section-title">Storage Information</label>
                <div className="yt-settings-storage-box">
                  <div className="yt-storage-stat-line">
                    <span>SpeedoTube Downloads</span>
                    <strong>{storageStats.usedFormatted}</strong>
                  </div>
                  <div className="yt-storage-stat-line">
                    <span>Available Free Space</span>
                    <strong>{storageStats.freeFormatted}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="yt-downloads-modal-footer">
              <button
                className="yt-downloads-modal-done-btn"
                onClick={closeSettingsModal}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete All Modal */}
      <ConfirmModal
        isOpen={isDeleteAllModalOpen}
        title="Delete all downloads?"
        message="Are you sure you want to delete all downloaded videos from this device? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        onConfirm={handleClearAll}
        onClose={() => setIsDeleteAllModalOpen(false)}
      />

      {/* Toast Notification */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        icon={toast.icon}
        onClose={() => setToast({ isOpen: false, message: '', icon: null })}
      />
    </div>
  );
}
